import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, Stack, Typography } from '@mui/material';
import VerticalAlignTopIcon from '@mui/icons-material/VerticalAlignTop';
import VerticalAlignBottomIcon from '@mui/icons-material/VerticalAlignBottom';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import { Document, Page, pdfjs } from 'react-pdf';
import useMeasure from 'react-use-measure';
import 'react-pdf/dist/Page/TextLayer.css';
import { getPdfWorkerSrc } from '@shared/utils/helpers/pdfWorker';
import { useTranslation } from '@shared/utils/i18n';
import {
  A4_RATIO,
  computePagesLayout,
  findPageIndexInDocument,
  getCurrentPageIndex,
  getRenderRange,
  type PdfTextSource
} from '@shared/ui/pdfViewer/lib';

/**
 * OSI-818: минимально нужная часть PDFDocumentProxy (структурно, без прямой зависимости
 * от pdfjs-dist — он приезжает транзитивно через react-pdf).
 */
interface PdfDocument extends PdfTextSource {
  getPage(pageNumber: number): Promise<{
    getTextContent(): Promise<{ items: Array<{ str?: string } | object> }>;
    getViewport(params: { scale: number }): { width: number; height: number };
  }>;
}

interface Props {
  base64: string;
  /**
   * OSI-818: текст, к странице с которым лента прокручивается при открытии (номер акта
   * текущего дома в общем акте). Не найден или не передан — документ открывается с начала.
   */
  scrollToText?: string;
  /** OSI-818: подпись кнопки «к найденной странице» на панели; кнопка есть, только если передан scrollToText. */
  scrollToLabel?: string;
  /** OSI-818: высота области просмотра (лента прокручивается внутри неё). */
  height?: number | string;
}

/** OSI-818: отступ между страницами и поля ленты, px. */
const PAGE_GAP = 12;
const LANE_PADDING = 12;

// Воркер берём со своего домена (public/pdf.worker.min.js), а не с CDN — см. pdfWorker.ts.
pdfjs.GlobalWorkerOptions.workerSrc = getPdfWorkerSrc(pdfjs.version);

/**
 * OSI-818: просмотр PDF лентой — все страницы подряд с вертикальной прокруткой внутри
 * области просмотра (раньше — одна страница на всю ширину и стрелки под ней).
 *
 * Ленивая отрисовка: размеры всех страниц берутся у pdf.js без рендера (getViewport),
 * по ним заранее считается раскладка (computePagesLayout) — каждая страница стоит на своём
 * месте заглушкой точной высоты, поэтому прокрутка не прыгает. Настоящий <Page> (canvas +
 * текстовый слой) монтируется только для страниц в окне «видимая область ± один её экран»
 * (getRenderRange); ушедшие из окна снова становятся заглушками. Так 40+ страниц общего
 * акта не грузят слабые устройства.
 *
 * Ширина страницы = ширина ленты (useMeasure, реагирует на resize), горизонтальной
 * прокрутки нет. Outline убран: у актов оглавления нет, а блок сдвигал вёрстку.
 */
export const PdfViewer: React.FC<Props> = observer(({ base64, scrollToText, scrollToLabel, height = '65vh' }) => {
  const { t } = useTranslation();

  const scrollElRef = useRef<HTMLDivElement | null>(null);
  const [viewportMeasureRef, { height: viewportHeight }] = useMeasure();
  const [laneMeasureRef, { width: laneWidth }] = useMeasure();

  const [pdf, setPdf] = useState<PdfDocument | null>(null);
  const [ratios, setRatios] = useState<number[] | null>(null);
  /** Индекс страницы с scrollToText: null — ещё ищем, -1 — не найдено / искать нечего. */
  const [targetIndex, setTargetIndex] = useState<number | null>(null);
  const [scrollTop, setScrollTop] = useState(0);

  const didInitialScrollRef = useRef(false);
  const prevPageWidthRef = useRef(0);

  const file = useMemo(() => `data:application/pdf;base64,${base64}`, [base64]);

  // Новый документ — сбрасываем всё, что относилось к прежнему.
  useEffect(() => {
    setPdf(null);
    setRatios(null);
    setTargetIndex(null);
    setScrollTop(0);
    didInitialScrollRef.current = false;
    if (scrollElRef.current) scrollElRef.current.scrollTop = 0;
  }, [base64]);

  const onLoadSuccess = useCallback((doc: PdfDocument) => {
    setPdf(doc);
  }, []);

  // Пропорции страниц — без рендера. Отмена по cleanup: диалог закрыли / документ сменился.
  useEffect(() => {
    if (!pdf) return undefined;

    let cancelled = false;
    const pageNumbers = Array.from({ length: pdf.numPages }, (_, i) => i + 1);

    Promise.all(
      pageNumbers.map((pageNumber) =>
        pdf.getPage(pageNumber).then((page) => {
          const viewport = page.getViewport({ scale: 1 });
          return viewport.width > 0 ? viewport.height / viewport.width : A4_RATIO;
        })
      )
    )
      .then((result) => {
        if (!cancelled) setRatios(result);
      })
      .catch(() => {
        // Документ уничтожен (закрыли диалог) или страница не читается — раскладка по A4.
        if (!cancelled) setRatios(pageNumbers.map(() => A4_RATIO));
      });

    return () => {
      cancelled = true;
    };
  }, [pdf]);

  // Поиск страницы с нужным текстом — через getTextContent, без рендера страниц.
  useEffect(() => {
    if (!pdf) return undefined;

    if (!scrollToText) {
      setTargetIndex(-1);
      return undefined;
    }

    let cancelled = false;

    findPageIndexInDocument(pdf, scrollToText, () => cancelled)
      .then((index) => {
        if (!cancelled) setTargetIndex(index);
      })
      .catch(() => {
        if (!cancelled) setTargetIndex(-1);
      });

    return () => {
      cancelled = true;
    };
  }, [pdf, scrollToText]);

  const numPages = pdf?.numPages ?? 0;
  const pageWidth = Math.max(0, Math.floor(laneWidth - 2 * LANE_PADDING));

  const layout = useMemo(
    () => (ratios && pageWidth > 0 ? computePagesLayout(ratios, pageWidth, PAGE_GAP, LANE_PADDING) : null),
    [ratios, pageWidth]
  );

  const [firstRendered, lastRendered] = layout
    ? getRenderRange(layout, scrollTop, viewportHeight, viewportHeight)
    : [0, -1];
  const currentIndex = layout ? getCurrentPageIndex(layout, scrollTop, viewportHeight) : 0;

  const scrollToIndex = useCallback(
    (index: number) => {
      const el = scrollElRef.current;
      if (!el || !layout || index < 0 || index >= layout.offsets.length) return;

      el.scrollTop = layout.offsets[index] - LANE_PADDING;
      setScrollTop(el.scrollTop);
    },
    [layout]
  );

  // Прокрутка к странице с нужным текстом при открытии — один раз на документ.
  // Если пользователь уже успел прокрутить сам (поиск по 40+ страницам не мгновенный) —
  // не дёргаем его.
  useLayoutEffect(() => {
    const el = scrollElRef.current;
    if (didInitialScrollRef.current || !el || !layout || targetIndex === null) return;

    didInitialScrollRef.current = true;
    if (targetIndex > 0 && el.scrollTop === 0) scrollToIndex(targetIndex);
  }, [layout, targetIndex, scrollToIndex]);

  // Смена ширины (resize окна): все высоты масштабируются пропорционально ширине —
  // так же масштабируем прокрутку, чтобы остаться на той же странице.
  useLayoutEffect(() => {
    const el = scrollElRef.current;
    const prevWidth = prevPageWidthRef.current;
    prevPageWidthRef.current = pageWidth;

    if (!el || prevWidth <= 0 || pageWidth <= 0 || prevWidth === pageWidth) return;

    el.scrollTop = Math.round(el.scrollTop * (pageWidth / prevWidth));
    setScrollTop(el.scrollTop);
  }, [pageWidth]);

  const setViewportEl = useCallback(
    (el: HTMLDivElement | null) => {
      scrollElRef.current = el;
      viewportMeasureRef(el);
    },
    [viewportMeasureRef]
  );

  const onScroll = useCallback(() => {
    if (scrollElRef.current) setScrollTop(scrollElRef.current.scrollTop);
  }, []);

  return (
    <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
      {/* OSI-818: панель над документом — всегда видна, лента прокручивается под ней. */}
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{
          px: 1.5,
          py: 0.5,
          flexWrap: 'wrap',
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: 'background.paper'
        }}
      >
        <Typography variant="body2" sx={{ flexGrow: 1 }}>
          {numPages
            ? t('common:pdfViewer.pageOf', { current: currentIndex + 1, total: numPages })
            : t('common:pdfViewer.loading')}
        </Typography>
        {scrollToText && (
          <Button
            size="small"
            startIcon={<MyLocationIcon />}
            disabled={!layout || targetIndex === null || targetIndex < 0}
            onClick={() => targetIndex !== null && scrollToIndex(targetIndex)}
          >
            {scrollToLabel || t('common:pdfViewer.toTarget')}
          </Button>
        )}
        <Button size="small" startIcon={<VerticalAlignTopIcon />} disabled={!layout} onClick={() => scrollToIndex(0)}>
          {t('common:pdfViewer.toStart')}
        </Button>
        <Button
          size="small"
          startIcon={<VerticalAlignBottomIcon />}
          disabled={!layout}
          onClick={() => scrollToIndex(numPages - 1)}
        >
          {t('common:pdfViewer.toEnd')}
        </Button>
      </Stack>

      <Box
        ref={setViewportEl}
        onScroll={onScroll}
        // overflowY: 'scroll' — полоса прокрутки есть всегда, ширина ленты не меняется при её
        // появлении (иначе страницы пересчитывались бы по кругу).
        sx={{ height, overflowY: 'scroll', overflowX: 'hidden', bgcolor: 'grey.200' }}
      >
        <Box ref={laneMeasureRef} sx={{ position: 'relative', height: layout ? layout.totalHeight : undefined }}>
          {base64 && (
            <Document
              file={file}
              onLoadSuccess={onLoadSuccess}
              loading={
                <Typography variant="body2" sx={{ p: 2 }}>
                  {t('common:pdfViewer.loading')}
                </Typography>
              }
            >
              {layout?.offsets.map((top, index) => (
                <Box
                  // eslint-disable-next-line react/no-array-index-key -- страница = индекс, порядок не меняется
                  key={index}
                  sx={{
                    position: 'absolute',
                    top,
                    left: LANE_PADDING,
                    width: pageWidth,
                    height: layout.heights[index],
                    overflow: 'hidden',
                    bgcolor: 'common.white',
                    boxShadow: 1
                  }}
                >
                  {index >= firstRendered && index <= lastRendered && (
                    <Page pageIndex={index} width={pageWidth} renderAnnotationLayer={false} loading={null} />
                  )}
                </Box>
              ))}
            </Document>
          )}
        </Box>
      </Box>
    </Box>
  );
});
